import { applyTheme, readTheme } from './theme';
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale, ThemeMode } from '../domain/types';
import { applyDocumentLanguage } from '../i18n';

interface PreferencesContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => void;
}

const PreferencesContext = createContext<PreferencesContextValue | null>(null);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [locale, setLocaleState] = useState<Locale>(() => (i18n.language === 'ar' ? 'ar' : 'de'));
  const [theme, setThemeState] = useState<ThemeMode>(readTheme);

  useEffect(() => {
    applyTheme(theme);
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const listener = () => theme === 'system' && applyTheme('system');
    media.addEventListener('change', listener);
    return () => media.removeEventListener('change', listener);
  }, [theme]);

  const value = useMemo<PreferencesContextValue>(
    () => ({
      locale,
      setLocale: (next) => {
        setLocaleState(next);
        applyDocumentLanguage(next);
        void i18n.changeLanguage(next);
      },
      theme,
      setTheme: (next) => {
        applyTheme(next);
        setThemeState(next);
      },
    }),
    [i18n, locale, theme],
  );

  return <PreferencesContext.Provider value={value}>{children}</PreferencesContext.Provider>;
}

export function usePreferences(): PreferencesContextValue {
  const value = useContext(PreferencesContext);
  if (!value) throw new Error('PreferencesProvider is missing');
  return value;
}
